import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import { PageNote } from "@/components/ui/page-legend";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { CennikBledyTable } from "@/features/contracts/cennik-bledy-table";
import type { ZgodnoscRow } from "@/features/contracts/contracts-api";
import { contractsApi } from "@/features/contracts/contracts-api";
import { getCurrentUser } from "@/lib/auth-api";
import { formatPLN } from "@/lib/format";
import { ensureAuthenticated } from "@/lib/route-guards";

// Backend dopuszcza tylko te role (poza nimi 403). Guard klienta odwzorowuje to,
// zeby analityk/superadmin wchodzili, a reszta dostawala redirect do dashboardu.
// Wzorzec identyczny jak w cenniki-historia.tsx.
const ALLOWED_ROLES = new Set(["super_admin", "analyst"]);

export const Route = createFileRoute("/_auth/kartoteka/cennik-zgodnosc")({
	component: CennikZgodnoscPage,
	beforeLoad: async () => {
		await ensureAuthenticated();
		const user = getCurrentUser();
		if (!user || !user.roles.some((r) => ALLOWED_ROLES.has(r))) {
			throw redirect({ to: "/dashboard" });
		}
	},
});

type Segment = "baza" | "franczyza";

/** Bezpieczna konwersja string->number (ceny przychodza jako string). null gdy brak/NaN. */
function toNum(v?: string): number | null {
	if (v == null || v === "") return null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
}

/** Cena z gwardia — "—" gdy brak/NaN. */
function cena(v?: string): string {
	const n = toNum(v);
	return n != null ? formatPLN(n) : "—";
}

/** Data+godzina (pl-PL) — "—" gdy brak. */
function fmtDateTime(iso: string | null): string {
	if (!iso) return "—";
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? "—"
		: d.toLocaleString("pl-PL", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
}

/** Rdzeń nazwy pliku KSEDI (np. "0F015288.XML") — FM1 dokleja z przodu czas z FTP
 * ("13_07_2026_06_01_01_0F015288.XML"), FM2 trzyma samą nazwę. Do porównania "ten sam
 * plik?" bierzemy człon po ostatnim "_" (dla FM2 bez "_" to cała nazwa). */
function plikCore(s: string | null): string | null {
	if (!s) return null;
	return s.trim().toUpperCase().split("_").pop() ?? null;
}

/** Różnica czasu między dwiema datami w formie "X min" / "X h Y min". Null gdy brak. */
function diffHuman(a: string | null, b: string | null): string | null {
	if (!a || !b) return null;
	const ms = Math.abs(new Date(a).getTime() - new Date(b).getTime());
	if (Number.isNaN(ms)) return null;
	const min = Math.round(ms / 60000);
	if (min < 60) return `${min} min`;
	const h = Math.floor(min / 60);
	const m = min % 60;
	return m ? `${h} h ${m} min` : `${h} h`;
}

const STATUS_META: Record<ZgodnoscRow["status"], { label: string; className: string }> = {
	zgodne: {
		label: "Zgodne",
		className: "border-success/30 bg-success/15 text-success",
	},
	rozjazd: {
		label: "Rozjazd",
		className: "border-destructive/30 bg-destructive/15 text-destructive",
	},
	tylko_fm2: {
		label: "Tylko FM2",
		className: "border-warning/30 bg-warning/15 text-warning",
	},
	tylko_fm1: {
		label: "Tylko FM1",
		className: "border-border bg-muted text-muted-foreground",
	},
};

function StatusBadge({ status }: { status: ZgodnoscRow["status"] }) {
	const meta = STATUS_META[status];
	return <Badge className={`text-[11px] ${meta.className}`}>{meta.label}</Badge>;
}

function SummaryCard({
	label,
	value,
	className,
}: {
	label: string;
	value: number;
	className: string;
}) {
	return (
		<Card>
			<CardContent className={`py-4 max-md:px-3 max-md:py-2 ${className}`}>
				<div className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</div>
				<div className="mt-1 text-2xl font-semibold tabular-nums max-md:mt-0 max-md:text-lg">
					{value}
				</div>
			</CardContent>
		</Card>
	);
}

function CennikZgodnoscPage() {
	const [idfdost, setIdfdost] = useState<number | null>(null);
	const [segment, setSegment] = useState<Segment>("baza");

	// Lista hurtowni ze skonfigurowanym źródłem cennika (super_admin + analyst).
	const { data: hurtownie = [] } = useQuery({
		queryKey: ["cenniki", "hurtownie"],
		queryFn: contractsApi.getCennikHurtownie,
	});

	const wholesalers = useMemo(
		() =>
			[...hurtownie].sort((a, b) =>
				(a.nazwa || `#${a.idfdost}`).localeCompare(b.nazwa || `#${b.idfdost}`, "pl"),
			),
		[hurtownie],
	);

	const canQuery = idfdost != null;

	const { data, isFetching, isLoading } = useQuery({
		queryKey: ["cenniki", "zgodnosc-fm1", idfdost, segment],
		queryFn: () => contractsApi.getZgodnoscFm1(idfdost as number, segment),
		enabled: canQuery,
	});

	// Błędy/anomalie pobranego cennika FTP (dziś multi_bloz) — tłumaczą rozjazdy.
	const { data: bledy = [] } = useQuery({
		queryKey: ["cenniki", "bledy", idfdost, segment],
		queryFn: () => contractsApi.getCennikBledy(idfdost as number, segment),
		enabled: canQuery,
	});

	const rozbieznosci = data?.rozbieznosci ?? [];

	// Telefon: hurtownia i segment w chipach/arkuszu filtrow zamiast dwoch pelnych pol.
	const selectedHurt = wholesalers.find((w) => w.idfdost === idfdost);
	const mobileHurtownia = (
		<Select
			value={idfdost != null ? String(idfdost) : undefined}
			onValueChange={(v) => setIdfdost(Number(v))}
		>
			<SelectTrigger className="h-10 w-full" aria-label="Hurtownia">
				<SelectValue placeholder="Wybierz hurtownię" />
			</SelectTrigger>
			<SelectContent>
				{wholesalers.map((w) => (
					<SelectItem key={w.idfdost} value={String(w.idfdost)}>
						{w.nazwa || `#${w.idfdost}`}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
	const mobileSegment = (
		<Select value={segment} onValueChange={(v) => setSegment(v as Segment)}>
			<SelectTrigger className="h-10 w-full" aria-label="Segment">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="baza">Baza (AW)</SelectItem>
				<SelectItem value="franczyza">Franczyza (AF)</SelectItem>
			</SelectContent>
		</Select>
	);

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="p-6 pb-2 max-md:px-3 max-md:pt-3 max-md:pb-1 max-md:empty:hidden">
				<PageHeader
					title="Zgodność cen FM2↔FM1"
					description="Porównanie ceny netto FM2 (nowy cennik FTP) z ceną netto FM1 (ofertyitem) — walidacja przed odcięciem FM1."
				/>
			</div>

			<MobileFilters
				chips={[
					{
						id: "hurtownia",
						label: "Hurtownia",
						value: selectedHurt
							? selectedHurt.nazwa || `#${selectedHurt.idfdost}`
							: "Wybierz hurtownię",
						active: idfdost != null,
						picker: mobileHurtownia,
					},
					{
						id: "segment",
						label: "Segment",
						value: segment === "baza" ? "Baza (AW)" : "Franczyza (AF)",
						active: segment !== "baza",
						picker: mobileSegment,
					},
				]}
				sheetContent={
					<>
						<MobileFilterField label="Hurtownia">{mobileHurtownia}</MobileFilterField>
						<MobileFilterField label="Segment">{mobileSegment}</MobileFilterField>
					</>
				}
				onClear={() => setSegment("baza")}
				resultLabel="Pokaż zgodność"
			>
				{/* Toolbar kontrolek */}
				<div className="px-6 py-3 border-y bg-card/50 grid grid-cols-1 gap-4 md:grid-cols-3">
					<div className="flex flex-col gap-1.5 md:col-span-2">
						<Label>Hurtownia</Label>
						<Select
							value={idfdost != null ? String(idfdost) : undefined}
							onValueChange={(v) => setIdfdost(Number(v))}
						>
							<SelectTrigger className="w-full">
								<SelectValue placeholder="Wybierz hurtownię" />
							</SelectTrigger>
							<SelectContent>
								{wholesalers.map((w) => (
									<SelectItem key={w.idfdost} value={String(w.idfdost)}>
										{w.nazwa || `#${w.idfdost}`}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="flex flex-col gap-1.5">
						<Label>Segment</Label>
						<Select value={segment} onValueChange={(v) => setSegment(v as Segment)}>
							<SelectTrigger className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="baza">Baza (AW)</SelectItem>
								<SelectItem value="franczyza">Franczyza (AF)</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
			</MobileFilters>

			<div className="flex-1 min-h-0 overflow-auto p-6 pt-4 space-y-4 max-md:p-3 max-md:space-y-3">
				{!canQuery ? (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Wybierz hurtownię.
						</CardContent>
					</Card>
				) : isLoading || isFetching ? (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Wczytywanie…
						</CardContent>
					</Card>
				) : !data ? (
					<Card>
						<CardContent className="py-16 text-center text-sm text-muted-foreground">
							Brak danych.
						</CardContent>
					</Card>
				) : (
					<>
						{/* Podsumowanie */}
						<div className="grid grid-cols-2 gap-4 md:grid-cols-4 max-md:gap-2">
							<SummaryCard label="Zgodne" value={data.zgodne} className="text-success" />
							<SummaryCard label="Rozjazd" value={data.rozjazd} className="text-destructive" />
							<SummaryCard label="Tylko FM2" value={data.tylkoFm2} className="text-warning" />
							<SummaryCard
								label="Tylko FM1"
								value={data.tylkoFm1}
								className="text-muted-foreground"
							/>
						</div>

						{/* Metadane cenników — data, plik, "ten sam plik?" (rozjazd cen = realny błąd
						    tylko gdy pliki się zgadzają; różne pliki => FM2 ma starszy/inny plik). */}
						{(() => {
							const p2 = data.cennikFm2Plik?.trim() || null;
							const p1 = data.cennikFm1Plik?.trim() || null;
							// Porównujemy rdzeń (FM1 dokleja czas FTP z przodu) — inaczej fałszywe "różne".
							const samePlik = p2 != null && p1 != null && plikCore(p2) === plikCore(p1);
							return (
								<div className="rounded-md border border-border bg-card/50 px-3 py-2 text-xs space-y-1">
									<div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-muted-foreground">
										<span>
											FM2 (FTP): wystawiony{" "}
											<span className="font-medium text-foreground">
												{fmtDateTime(data.cennikFm2At)}
											</span>
											, pobrany{" "}
											<span className="font-medium text-foreground">
												{fmtDateTime(data.cennikFm2Pobrano)}
											</span>
										</span>
										<span>
											FM1 (najnowsza oferta): wystawiony{" "}
											<span className="font-medium text-foreground">
												{fmtDateTime(data.cennikFm1At)}
											</span>
										</span>
										{diffHuman(data.cennikFm2At, data.cennikFm1At) && (
											<span>
												· różnica wystawienia: {diffHuman(data.cennikFm2At, data.cennikFm1At)}
											</span>
										)}
									</div>
									<div className="flex flex-wrap items-center gap-x-4 gap-y-1">
										<span className="text-muted-foreground">
											Plik FM2: <span className="font-mono text-foreground">{p2 ?? "—"}</span>
										</span>
										<span className="text-muted-foreground">
											Plik FM1: <span className="font-mono text-foreground">{p1 ?? "—"}</span>
										</span>
										{p2 != null && p1 != null && (
											<Badge
												className={
													samePlik
														? "text-[11px] border-success/30 bg-success/15 text-success"
														: "text-[11px] border-warning/30 bg-warning/15 text-warning"
												}
											>
												{samePlik ? "TEN SAM PLIK" : "RÓŻNE PLIKI — FM2 starszy/inny"}
											</Badge>
										)}
									</div>
									{p2 != null && p1 != null && !samePlik && (
										<div className="text-[11px] text-muted-foreground">
											Rozjazdy to najpewniej różnica plików (FM2 dogoni po następnym pobraniu, co
											~15 min). Realny błąd cen tylko gdy pliki się zgadzają.
										</div>
									)}
								</div>
							);
						})()}

						{/* Błędy cennika FM2 (multi_bloz) — najczęstsza przyczyna rozjazdu.
						    Bloz w pliku FTP >1× z różną ceną; fetcher bierze najniższą, FM1 inny wariant. */}
						{bledy.length > 0 && (
							<Card>
								<CardContent className="px-4 py-4 space-y-3">
									<div className="flex flex-wrap items-center gap-2">
										<Badge className="text-[11px] border-warning/30 bg-warning/15 text-warning">
											Błędy cennika FM2: {bledy.length}
										</Badge>
										<span className="text-xs text-muted-foreground">
											Bloz z &gt;1 różną ceną w pliku FTP — wybieramy najniższą. To najczęstsza
											przyczyna rozjazdu z FM1.
										</span>
									</div>
									<CennikBledyTable bledy={bledy} />
								</CardContent>
							</Card>
						)}

						{/* Tabela rozbieżności */}
						<Card>
							<CardContent className="px-4 py-4 space-y-3 max-md:px-3">
								<PageNote id="list-scope" title="Zakres listy">
									<div className="text-xs text-muted-foreground">
										Pokazujemy do 500 rozbieżności; zgodne pomijamy.
									</div>
								</PageNote>
								<div className="overflow-auto rounded-md border border-border">
									<table className="w-full text-xs border-collapse">
										<thead>
											<tr className="border-b border-border bg-muted/50 text-left text-muted-foreground">
												<th className="px-3 py-2 font-medium">BLOZ</th>
												<th className="px-3 py-2 font-medium">Nazwa</th>
												<th className="px-3 py-2 font-medium text-right">Cena FM2</th>
												<th className="px-3 py-2 font-medium text-right">Cena FM1</th>
												<th className="px-3 py-2 font-medium">Status</th>
											</tr>
										</thead>
										<tbody>
											{rozbieznosci.length === 0 ? (
												<tr>
													<td colSpan={5} className="px-3 py-10 text-center text-muted-foreground">
														Brak rozbieżności.
													</td>
												</tr>
											) : (
												rozbieznosci.map((r, i) => (
													<tr
														// biome-ignore lint/suspicious/noArrayIndexKey: rozbieznosci tylko do odczytu; BLOZ moze sie powtorzyc.
														key={`${r.bloz07}-${i}`}
														className="border-b border-border last:border-0 hover:bg-muted/50"
													>
														<td className="px-3 py-2 font-mono tabular-nums">{r.bloz07}</td>
														<td className="px-3 py-2">{r.nazwa || "—"}</td>
														<td className="px-3 py-2 text-right font-mono tabular-nums">
															{cena(r.cenaFm2)}
														</td>
														<td className="px-3 py-2 text-right font-mono tabular-nums">
															{cena(r.cenaFm1)}
														</td>
														<td className="px-3 py-2">
															<StatusBadge status={r.status} />
														</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								</div>
							</CardContent>
						</Card>
					</>
				)}
			</div>
		</div>
	);
}
